// Composer (job page round): what someone writes into a record's history, and who it's for: a note for
// the office, a message to the techs on the job, a text to the customer. Who it's for is a Segmented
// switch; the box says where it goes; the button says what it does ("Text Jordan"). Sending keeps the
// words and the button dashed; one that fails says so in red and keeps the words; sent, the box clears.
// Reads only --compose-*; Segmented, Field and Button read their own.
import { useState, type FormEvent, type KeyboardEvent } from 'react';
import { Button } from '../Button/Button.tsx';
import { TextField } from '../Field/Field.tsx';
import { Segmented } from '../Segmented/Segmented.tsx';
import './Composer.css';

export interface ComposeMode {
  id: string;
  /** On the switch: "Note", "Message the techs", "Text Jordan". */
  label: string;
  /** In the empty box: "Add a note for the office…". */
  placeholder: string;
  /** On the button: "Add", "Send", "Text Jordan". */
  sendLabel: string;
  /** Under the box: who sees it ("Only the office sees notes."). */
  note?: string;
}

export interface ComposerProps {
  modes: ComposeMode[];
  /** Sends it; a rejection keeps the words and says it didn't go. */
  onSend: (mode: string, text: string) => Promise<void> | void;
  /** Its name for screen readers ("Write in the job’s history"). */
  label?: string;
  /** Storybook only: draws it sending, or failed. */
  previewState?: 'sending' | 'failed';
  /** Enter sends and Shift+Enter starts a new line, as a chat's box does (Ask the analyst). */
  sendOnEnter?: boolean;
}

export function Composer({
  modes,
  onSend,
  label = 'Write in the history',
  previewState,
  sendOnEnter = false,
}: ComposerProps) {
  const [mode, setMode] = useState(modes[0]!.id);
  const [text, setText] = useState(previewState ? 'The city books gas inspections within 5 days.' : '');
  const [busy, setBusy] = useState(previewState === 'sending');
  const [error, setError] = useState<string | null>(
    previewState === 'failed' ? 'It didn’t send. Your words are still here; try again.' : null,
  );
  const m = modes.find((x) => x.id === mode) ?? modes[0]!;
  const submit = async (e: FormEvent | KeyboardEvent) => {
    e.preventDefault();
    if (busy || !text.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await onSend(mode, text.trim());
      setText('');
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'It didn’t send. Your words are still here; try again.',
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fl-compose"
      aria-label={label}
      onSubmit={(e) => void submit(e)}
      {...(sendOnEnter
        ? {
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' && !e.shiftKey && (e.target as HTMLElement).tagName === 'TEXTAREA')
                void submit(e);
            },
          }
        : {})}
    >
      {modes.length > 1 ? (
        <Segmented
          label="Who it’s for"
          selectedKey={mode}
          onSelectionChange={setMode}
          items={modes.map((x) => ({ id: x.id, label: x.label }))}
        />
      ) : null}
      <div className="fl-compose__row">
        <div className="fl-compose__box">
          <TextField
            label={m.label}
            hideLabel
            multiline
            placeholder={m.placeholder}
            value={text}
            onChange={setText}
            isDisabled={busy}
            description={m.note}
            error={error}
          />
        </div>
        <Button
          type="submit"
          variant={text.trim() ? 'primary' : 'secondary'}
          isBusy={busy}
          isDisabled={!text.trim() && !busy}
        >
          {m.sendLabel}
        </Button>
      </div>
    </form>
  );
}
